import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { APIURL, TZ } from '../../../Global';
import axios from 'axios';
import './VendorRevenue.css';
import { useTranslation } from 'react-i18next';
import { formatDateString, formatDateForFilename } from '../../../utils/dateUtils';

const VendorDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const vendorNameFromState = state && state.vendorName ? state.vendorName : '';
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);

  const heading = useMemo(() => vendorNameFromState || `Vendor ${id}`, [vendorNameFromState, id]);

  // Initialize dates to current month
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDayOfMonth.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo && id) {
      fetchDetails();
    }
  }, [id, dateFrom, dateTo, selectedFilter]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const tokenString = sessionStorage.getItem('usertoken');
      const str = tokenString ? tokenString.replace(/["]+/g, '') : '';

      // Build query params
      let queryParams = `start_date=${dateFrom}&end_date=${dateTo}&hospital_id=${id}&${TZ}`;

      // Add type filter if not 'all'
      if (selectedFilter !== 'all') {
        queryParams += `&type=${selectedFilter}`;
      }

      const res = await axios.get(`${APIURL}/api/v1/staff/orders-list/?${queryParams}`, {
        headers: { Authorization: 'Token ' + str },
      });

      console.log('API Response:', res.data);

      if (res.data && res.data.status === 'success' && Array.isArray(res.data.detail)) {
        // Check if the detail array is empty
        if (res.data.detail.length === 0) {
          setItems([]);
          return;
        }

        // Transform API data to match expected format
        const transformedData = res.data.detail.map(item => ({
          name: item.service_name || item.product_name || 'N/A',
          category: item.category || item.service_type || 'General',
          quantity: item.quantity || 1,
          revenue: item.total_amount || item.amount || 0,
          order_id: item.order_id || item.id || 'N/A',
          invoice: item.invoice_number || `INV-${item.order_id || item.id}`,
          date: item.order_date || item.created_date || new Date().toISOString().split('T')[0],
          currency: item.currency || 'CRC',
          type: item.type || 'product',
          original_data: item
        }));

        setItems(transformedData);
      } else {
        // If no data or unexpected format, set empty array
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Format currency with CRC symbol
  const formatCurrency = (amount, currency = 'CRC') => {
    if (currency === 'CRC') {
      return `₡${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const handleViewInvoice = (row) => {
    // Prefer a direct URL if provided by API
    if (row.original_data && row.original_data.invoice_url) {
      window.open(row.original_data.invoice_url, '_blank');
      return;
    }

    // Fallback: open a minimal printable invoice view using available row data
    const w = window.open('', '_blank');
    if (!w) return;
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin: 0 0 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        td { padding: 6px 8px; }
        .muted { color: #666; }
        .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
      </style>
    `;
    const html = `
      <html>
        <head><title>Invoice ${row.invoice}</title>${styles}</head>
        <body>
          <div class="header">
            <h1>Invoice ${row.invoice}</h1>
            <div>Date: ${row.date}</div>
          </div>
          <table>
            <tr><td class="muted">Vendor</td><td>${heading}</td></tr>
            <tr><td class="muted">Hospital ID</td><td>${id}</td></tr>
            <tr><td class="muted">Order ID</td><td>${row.order_id}</td></tr>
            <tr><td class="muted">Item</td><td>${row.name}</td></tr>
            <tr><td class="muted">Category</td><td>${row.category}</td></tr>
            <tr><td class="muted">Quantity</td><td>${row.quantity}</td></tr>
            <tr><td class="muted">Amount</td><td>${formatCurrency(row.revenue, row.currency)}</td></tr>
          </table>
        </body>
      </html>
    `;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const formatCSVField = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).replace(/"/g, '""');
    if (/[",\n]/.test(str)) return `"${str}"`;
    return str;
  };

  const sanitizeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    return /^[=+\-@]/.test(str) ? `'${str}` : str;
  };

  const handleExportCSV = () => {
    if (!items || items.length === 0) return;
    const header = ['Item Name', 'Category', 'Quantity', 'Total Revenue', 'Order ID', 'Invoice', 'Date'];
    const rows = items.map(r => [
      sanitizeCSV(r.name),
      sanitizeCSV(r.category),
      r.quantity,
      r.revenue,
      sanitizeCSV(r.order_id),
      sanitizeCSV(r.invoice),
      sanitizeCSV(r.date)
    ]);
    const csvContent = [header, ...rows].map(r => r.map(formatCSVField).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor_${id}_items_${formatDateForFilename()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const style = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin: 0 0 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; }
        .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
      </style>
    `;
    const thead = `<tr><th>Item Name</th><th>Category</th><th>Quantity</th><th>Total Revenue</th><th>Order ID</th><th>Invoice</th><th>Date</th></tr>`;
    const tbody = items.map(r => `
      <tr>
        <td>${sanitizeCSV(r.name)}</td>
        <td>${sanitizeCSV(r.category)}</td>
        <td>${r.quantity}</td>
        <td>${formatCurrency(r.revenue, r.currency)}</td>
        <td>${sanitizeCSV(r.order_id)}</td>
        <td>${sanitizeCSV(r.invoice)}</td>
        <td>${sanitizeCSV(r.date)}</td>
      </tr>
    `).join('');
    const html = `
      <html>
        <head>
          <title>${heading}</title>
          ${style}
        </head>
        <body>
          <div class="header">
            <h1>${heading} - Item Details</h1>
            <div>Hospital ID: ${id}</div>
          </div>
          <table>
            <thead>${thead}</thead>
            <tbody>${tbody}</tbody>
          </table>
        </body>
      </html>
    `;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  // Filter options
  const filterOptions = [
    { key: 'all', label: t('vendorDetails.filters.all') },
    { key: 'products', label: t('vendorDetails.filters.products') },
    { key: 'services', label: t('vendorDetails.filters.services') }
  ];

  return (
    <div className="vendor-details-page" style={{ background: '#f6fafd', width: '100%', minHeight: '100vh' }}>
      <div className="revenue-summary-section" style={{ marginBottom: 12 }}>
        <div className="revenue-summary-left" style={{ width: '100%' }}>
          {/* Header with title, export button, and back button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="revenue-summary-title" style={{ margin: 0 }}>{heading}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="export-dropdown">
                <button className="export-btn" onClick={() => setExportOpen(v => !v)}>
                  {t('vendorDetails.filters.export')} ▼
                </button>
                {exportOpen && (
                  <div className="export-menu">
                    <button onClick={() => { handleExportCSV(); setExportOpen(false); }}>
                      {t('vendorDetails.filters.exportCSV')}
                    </button>
                    <button onClick={() => { handleExportPDF(); setExportOpen(false); }}>
                      {t('vendorDetails.filters.exportPDF')}
                    </button>
                  </div>
                )}
              </div>
              <button className="filter-btn" onClick={() => navigate(-1)}>{t('vendorDetails.back')}</button>
            </div>
          </div>

          {/* Filters row */}
          <div className="revenue-filters" style={{ marginTop: 12 }}>
            <div className="date-filters" style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                <div className="date-filter-group1">
                  <label>{t('vendorDetails.filters.fromDate')}</label>
                  <input
                    type="date"
                    className="search-input1"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="date-filter-group1">
                  <label>{t('vendorDetails.filters.toDate')}</label>
                  <input
                    type="date"
                    className="search-input1"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>

              {/* Filter Buttons moved to right */}
              <div className="filter-buttons-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                {filterOptions.map(option => (
                  <button
                    key={option.key}
                    className={`filter-btn ${selectedFilter === option.key ? 'active' : ''}`}
                    onClick={() => setSelectedFilter(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="revenue-table-container" style={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div className="text-center py-5" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t('vendorDetails.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="no-data" style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            fontSize: '16px',
            color: '#666',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            {t('vendorDetails.noOrders')}
          </div>
        ) : (
          <>
            <div className="revenue-table-header revenue-grid" style={{ gridTemplateColumns: '20% 16% 10% 16% 12% 12% 14%' }}>
              <div><b>{t('vendorDetails.headers.itemName')}</b></div>
              <div><b>{t('vendorDetails.headers.category')}</b></div>
              <div><b>{t('vendorDetails.headers.quantity')}</b></div>
              <div><b>{t('vendorDetails.headers.totalRevenue')}</b></div>
              <div><b>{t('vendorDetails.headers.orderId')}</b></div>
              <div><b>{t('vendorDetails.headers.invoice')}</b></div>
              <div><b>{t('vendorDetails.headers.date')}</b></div>
            </div>
            {items.map((row, idx) => (
              <div key={idx} className="revenue-table-row revenue-grid" style={{ gridTemplateColumns: '20% 16% 10% 16% 12% 12% 14%' }}>
                <div className="truncate" style={{ fontWeight: 600 }}>{row.name}</div>
                <div className="truncate">{row.category}</div>
                <div>{row.quantity}</div>
                <div style={{ color: '#1ec6b6', fontWeight: 600 }}>{formatCurrency(row.revenue, row.currency)}</div>
                <div>{row.order_id}</div>
                <div>
                  <button className="view-btn" onClick={() => handleViewInvoice(row)}>
                    {t('vendorDetails.viewInvoice')}
                  </button>
                </div>
                <div>{formatDateString(row.date)}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default VendorDetails;